/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Lock } from '@saleshandy/icons';
import { SkeletonLoading } from '@saleshandy/design-system';
import { useTranslation } from 'react-i18next';
import { isValueBasedPlan } from '../../../../../../components/settings/components/billing-subscription/components/outreach-plans/utils/helper';
import { SubscriptionPlans } from '../../../../../utils/subscription-plans';
import {
  getCoreFeaturesCreditsRequest,
  getPostLoadMetaRequest,
} from '../../../../../../components/home/extra-actions';
import { constants } from '../../../../../enums/constants';
import { executeOnRequestStatus } from '../../../../../utils';
import { useCreditsSelectors } from './credits-box-helper';
import { resetCoreFeaturesCreditsRequestStatus } from '../../../../../../components/home/home-slice';
import { OverlayTooltip, Placement } from '../../../overlay';
import { RootState } from '../../../../../../store/root-reducer';
import FeatureCodes from '../../../../../enums/feature-codes';
import {
  computeLeadRevealCredits,
  formatLeadFinderCredits,
} from '../../../../../../components/v2-lead/helpers/helper';

function CreditsBox({ dropdownIsOpen, closeDropdown }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const featureUsages = useSelector(
    (state: RootState) => state?.home?.featureUsages,
  );

  const isProspectMaxFeatureActive = featureUsages?.find(
    (usage) => usage?.featureCode === FeatureCodes.ProspectMaxActive,
  );
  const isProspectEnrichmentEnabledMeta = useSelector(
    (state: RootState) => state?.home?.isProspectEnrichmentEnabled,
  );
  const isOldAgencyUser = useSelector(
    (state: RootState) => state?.home?.isAgencyUser,
  );

  const [hoveredCredit, setHoveredCredit] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); // Loading state to control skeleton visibility

  const {
    planCode,
    planType,
    leadFinderCreditsAvailable,
    emailSendAvailable,
    emailSendTotal,
    activeProspectAvailable,
    activeProspectTotal,
    aiCreditsAvailable,
    prospectAddAvailable,
    prospectAddTotal,
    emailVerificationAvailable,
    getCoreFeaturesCreditsRequestStatus,
  } = useCreditsSelectors();

  const leadReveal = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
      'LEAD.REVEAL'
      ],
  );
  const leadRevealBucket1 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
      'LEAD.REVEAL.BUCKET1'
      ],
  );
  const leadRevealBucket2 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
      'LEAD.REVEAL.BUCKET2'
      ],
  );
  const isBucket1Frozen = useSelector(
    (state: RootState) => state?.home?.leadFinder?.isBucket1Frozen,
  );

  const availableCredits = computeLeadRevealCredits(
    leadReveal,
    leadRevealBucket1,
    leadRevealBucket2,
    { isBucket1Frozen },
  );

  useEffect(() => {
    executeOnRequestStatus({
      status: getCoreFeaturesCreditsRequestStatus,
      onSuccess: () => setLoading(false),
      onFailed: () => setLoading(false),
      onPending: () => setLoading(true),
    });
  }, [getCoreFeaturesCreditsRequestStatus, dropdownIsOpen]);

  useEffect(() => {
    if (dropdownIsOpen) {
      // Reset the status to 'ideal' to allow re-fetching
      dispatch(resetCoreFeaturesCreditsRequestStatus());

      // Now dispatch the request to fetch data
      dispatch(getCoreFeaturesCreditsRequest());
      dispatch(getPostLoadMetaRequest());
    }
  }, [dropdownIsOpen, dispatch]);

  const parseOrZero = (val: string | number) =>
    parseInt(val as string, 10) || 0;

  const creditsData = useMemo(
    () => [
      {
        title: t('labels.lead_finder_credits'),
        available: parseFloat(String(leadFinderCreditsAvailable || 0)) || 0,
        path: '/billing/upgrade-plan/lead-finder',
        tooltip: 'By purchasing credits, you can reveal both emails and phone numbers of leads',
      },
      ...(!isProspectEnrichmentEnabledMeta || isOldAgencyUser
        ? []
        : [
          {
            title: t('labels.ai_credits'),
            available: Math.ceil(parseFloat(aiCreditsAvailable)) || 0,
            path: '/billing/subscriptions/ai-credits?openPaymentPopup=true',
            tooltip: 'By Purchasing Credits, you will get to use AI features like AI Sequences and AI Columns to automate and scale your outreach',
          },
        ]),
      {
        title: t('labels.email_verification'),
        available: parseOrZero(emailVerificationAvailable) || 0,
        path: '/billing/subscriptions/ev-credits?openPaymentPopup=true',
        tooltip: 'By Purchasing credits, you will get advanced email verification and deliverability insights that keep you out of spam folders',
      },
    ],
    [
      t,
      leadFinderCreditsAvailable,
      emailVerificationAvailable,
      aiCreditsAvailable,
    ],
  );

  const additionalCreditsData = useMemo(
    () => [
      {
        title: t('labels.monthly_emails_sent'),
        available: parseOrZero(emailSendAvailable),
        total: parseOrZero(emailSendTotal),
      },
      isProspectMaxFeatureActive
        ? {
          title: 'Active Prospects',
          available: parseOrZero(activeProspectAvailable),
          total: parseOrZero(activeProspectTotal),
        }
        : {
          title: t('labels.total_prospects'),
          available: parseOrZero(prospectAddAvailable),
          total: parseOrZero(prospectAddTotal),
        },
    ],
    [
      t,
      emailSendAvailable,
      emailSendTotal,
      isProspectMaxFeatureActive,
      activeProspectAvailable,
      activeProspectTotal,
      prospectAddAvailable,
      prospectAddTotal,
    ],
  );

  const calculateRemainingPercentage = (
    available: number,
    total: number,
  ): number => {
    if (!total || total <= 0) return 0; // Avoid division by zero or negative totals
    return Math.round((available / total) * 100);
  };

  const getRemainingColor = (available: number, title: string) => {
    const thresholds = {
      [t('labels.lead_finder_credits')]: constants.LEAD_REVEAL_BOTTOM_LIMIT,
      [t(
        'labels.email_verification',
      )]: constants.EMAIL_VERIFICATION_BOTTOM_LIMIT,
      [t('labels.ai_credits')]: constants.AI_CREDITS_BOTTOM_LIMIT,
    };
    return available < thresholds[title]
      ? 'credit-available-red'
      : 'credit-available-normal';
  };

  const isBelowThreshold = (available: number, title: string) => 
    // const thresholds = {
    //   [t('labels.lead_finder_credits')]: constants.LEAD_REVEAL_BOTTOM_LIMIT,
    //   [t(
    //     'labels.email_verification',
    //   )]: constants.EMAIL_VERIFICATION_BOTTOM_LIMIT,
    //   [t('labels.ai_credits')]: constants.AI_CREDITS_BOTTOM_LIMIT,
    // };
    // return available < thresholds[title];
     true
  ;

  const handleMouseEnter = useCallback(
    (title: string) => setHoveredCredit(title),
    [],
  );
  const handleMouseLeave = useCallback(() => setHoveredCredit(null), []);

  return (
    <div className="credits-box">
      <div className="section-without-progress-bar">
        {creditsData.map((credit) => (
          <div
            className={`feature-without-progress-bar ${credit.title === 'Lead Finder Credits' ? 'lead-finder-margin' : ''
              }`}
            key={credit.title}
            onMouseEnter={() => handleMouseEnter(credit.title)}
            onMouseLeave={handleMouseLeave}
          >
            <div className="credit-available-option">
              <span className="feature-credit-title">{credit.title}</span>
              <span
                className={`${getRemainingColor(
                  credit.available,
                  credit.title,
                )}`}
              >
                {loading ? (
                  <SkeletonLoading height={16} width={104} />
                ) : hoveredCredit === credit.title &&
                  isBelowThreshold(credit.available, credit.title) ? (
                  <OverlayTooltip
                    placement={Placement.Top}
                    text={credit.tooltip}
                    className="lead-finder-add-credits-tooltip"
                  >
                    <div className="add-credits-link">
                      <Link
                        to={credit.path}
                        className="addcredits-link"
                        onClick={() => {
                          closeDropdown();
                        }}
                      >
                        Add Credits <ArrowUpRight />
                      </Link>
                    </div>
                  </OverlayTooltip>
                ) : credit.title === t('labels.lead_finder_credits') && isBucket1Frozen === 1 ? (
                  (() => {
                    const planCreditsValue = leadReveal != null
                      ? Number(leadReveal)
                      : Number(leadRevealBucket1 ?? 0);
                    const bucket2Val = Number(leadRevealBucket2 ?? 0);
                    return (
                      <OverlayTooltip
                        placement={Placement.Bottom}
                        className="bucket-frozen-info-tooltip"
                        text={
                          <div className="bucket-frozen-info-tooltip__content">
                            <div className="bucket-frozen-info-tooltip__row">
                              <span className="bucket-frozen-info-tooltip__value">
                                {formatLeadFinderCredits(planCreditsValue)} (Frozen)
                              </span>
                              <span className="bucket-frozen-info-tooltip__desc">
                                Purchase the plan to access these credits.
                              </span>
                            </div>
                            {bucket2Val > 0 && (
                              <div className="bucket-frozen-info-tooltip__row">
                                <span className="bucket-frozen-info-tooltip__value">
                                  {formatLeadFinderCredits(bucket2Val)} (Available)
                                </span>
                                <span className="bucket-frozen-info-tooltip__desc">
                                  Currently, free credits are available to use.
                                </span>
                              </div>
                            )}
                          </div>
                        }
                      >
                        <div className="d-inline-flex align-items-center gap-8 cursor-pointer">
                          {formatLeadFinderCredits(availableCredits.totalCredits)}
                          <Lock
                            height={16}
                            width={16}
                            className="bucket-frozen-info-icon"
                          />
                        </div>
                      </OverlayTooltip>
                    );
                  })()
                ) : credit.title === t('labels.lead_finder_credits') ? (
                  formatLeadFinderCredits(availableCredits.totalCredits)
                ) : (
                  credit.available.toLocaleString()
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Conditional Divider and Additional Credits */}
      {(isValueBasedPlan(planType) ||
        planCode === SubscriptionPlans.TRIAL ||
        planCode === SubscriptionPlans.FREE) && (
          <>
            <div className="divider-inside-credit-box" />

            <div className="section-with-progress-bar">
              {additionalCreditsData.map((credit) => {
                const creditsUsedPercentage =
                  100 -
                  calculateRemainingPercentage(credit.available, credit.total);
                return (
                  <div
                    className="feature-with-progress-bar"
                    key={credit.title + creditsData.length}
                    onMouseEnter={() => handleMouseEnter(credit.title)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="totalandused-credit-option">
                      <span className="feature-credit-title">{credit.title}</span>
                      <span className="number-link-part">
                        {loading ? (
                          <SkeletonLoading height={16} width={104} />
                        ) : hoveredCredit === credit.title &&
                          creditsUsedPercentage >= 85 ? (
                          <div className="upgrade-link-feature-dropdown">
                            <Link
                              to="/billing/subscriptions/email-outreach"
                              className="upgrade-link-dropdown"
                              onClick={() => {
                                closeDropdown();
                              }}
                            >
                              Upgrade <ArrowUpRight />
                            </Link>
                          </div>
                        ) : (
                          <>
                            <div className="number-part-whole">
                              <span className="credit-used-number">
                                {(
                                  credit.total - credit.available
                                ).toLocaleString()}
                              </span>{' '}
                              <span className="class-for-slash">
                                &nbsp;/&nbsp;
                              </span>
                              <span className="credit-feature-total">
                                {credit.total.toLocaleString()}
                              </span>
                            </div>
                          </>
                        )}
                      </span>
                    </div>
                    {loading ? (
                      <SkeletonLoading width={272} height={6} />
                    ) : (
                      <div className="progress-bar ">
                        <div
                          className={`progress-fill  ${creditsUsedPercentage >= 85 ? 'warning' : 'normal'
                            }`}
                          style={{ width: `${creditsUsedPercentage}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
    </div>
  );
}

export default CreditsBox;
